import type { ReactNode } from 'react';
import type { Issue } from '@/lib/intake/rules';
import { issueMessage } from '@/lib/intake/rules';
import { COPY } from '../copy';
import { describedBy } from './describedBy';
import styles from './fields.module.css';

export interface FieldFrameProps {
  id: string;
  label: string;
  required: boolean;
  hint: string | undefined;
  issues: readonly Issue[];
  children: ReactNode;
}

function Marker({ required }: { required: boolean }) {
  return <span className={styles.marker}>{required ? COPY.required : COPY.optional}</span>;
}

function Messages({ id, issues }: { id: string; issues: readonly Issue[] }) {
  if (issues.length === 0) return null;
  return (
    <p id={`${id}-message`} className={styles.message}>
      {issues.map((issue) => (
        <span key={issue.code} className={issue.severity === 'ERROR' ? styles.error : styles.warning}>
          {issueMessage(issue)}{' '}
        </span>
      ))}
    </p>
  );
}

/** One control with a <label>: text, text area, select, date. */
export function LabelledField({ id, label, required, hint, issues, children }: FieldFrameProps) {
  return (
    <div className={styles.field}>
      <label htmlFor={id} className={styles.label}>
        {label} <Marker required={required} />
      </label>
      {hint && (
        <p id={`${id}-hint`} className={styles.hint}>
          {hint}
        </p>
      )}
      {children}
      <Messages id={id} issues={issues} />
    </div>
  );
}

/**
 * A group of controls under a <legend>: yes/no and several choices. The prototype's
 * labels pointed at an input id that did not exist; a fieldset is what names a
 * group (SPEC, Non-functional).
 */
export function GroupedField({ id, label, required, hint, issues, children }: FieldFrameProps) {
  return (
    <fieldset className={styles.field} aria-describedby={describedBy(id, hint, issues)}>
      <legend className={styles.label}>
        {label} <Marker required={required} />
      </legend>
      {hint && (
        <p id={`${id}-hint`} className={styles.hint}>
          {hint}
        </p>
      )}
      {children}
      <Messages id={id} issues={issues} />
    </fieldset>
  );
}
